import React, { useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Skeleton,
  Alert,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { getUserSessions } from "../../../store/features/getUserSessions";
import { ROOT_URL } from "../../../api/ApiConfig";
import { emitAgentSetup } from '../../../api/createAppSocket';
import { forceAuthExpiry } from '../../../api/authHttp';
import { useSocket } from "../../../../src/SocketContext";

export default function SessionsManagement() {
  const dispatch = useDispatch();
  const socket = useSocket();

  const userData = useSelector((state) => state.loginToken);
  const currentSessionId = userData?.response?.sessionId;
  const userId = userData?.response?.user?._id;
  console.log('sessionId', currentSessionId)

  const { sessions = [], loading, error } = useSelector(
    (state) => state.getUserSessions
  );

  const handleForceLogout = useCallback(
    ({ sessionId }) => {
      if (sessionId === currentSessionId) {
        forceAuthExpiry();
      }
    },
    [currentSessionId],
  );

  useEffect(() => {
    if (!socket || !userId || !currentSessionId) return undefined;

    if (!socket.connected) socket.connect();
    emitAgentSetup(socket);

    const joinSessionRoom = () => {
      socket.emit("join-session", { sessionId: currentSessionId });
    };

    joinSessionRoom();
    socket.on("connect", joinSessionRoom);
    socket.on("force-logout", handleForceLogout);
    return () => {
      socket.off("connect", joinSessionRoom);
      socket.off("force-logout", handleForceLogout);
    };
  }, [socket, userId, currentSessionId, handleForceLogout]);

  // Fetch sessions on mount
  useEffect(() => {
    if (userId) dispatch(getUserSessions());
  }, [dispatch, userId]);

  // Logout specific device/session
  const handleLogoutDevice = async (sessionId) => {
    try {
      console.log(`Logging out session: ${sessionId}`);

      const res = await fetch(`${ROOT_URL}/master-user/logout/${sessionId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Logout failed: ${res.status} - ${errorText}`);
      }

      if (socket && socket.connected) {
        socket.emit("session-logout", { sessionId });
        console.log(`Emitted session-logout for: ${sessionId}`);
      }

      // Refresh sessions after delay
      setTimeout(() => dispatch(getUserSessions()), 500);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Sort sessions: current first, then by last active
  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.sessionId === currentSessionId) return -1;
    if (b.sessionId === currentSessionId) return 1;
    return new Date(b.lastUsed) - new Date(a.lastUsed);
  });

  const skeletonRows = Array.from({ length: 5 });


  return (
    <Box sx={{ padding: "24px", paddingTop: '16px' }}>
      <Typography sx={{
        color: "#061522",
        fontFamily: "Inter",
        fontSize: "18px",
        fontWeight: 500,
        lineHeight: "18px",
        marginBottom: "8px",
      }}>
        Sessions
      </Typography>
      <Typography sx={{
        color: "#405261",
        fontFamily: "Inter",
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "22px",

      }}>
        Save time with pre-written replies that your team can use in chats and tickets
      </Typography>

      {error && <Typography color="error">{error}</Typography>}

      <TableContainer>
        <Table sx={{ marginTop: "18px" }}>
          <TableHead>
            <TableRow sx={{ "& th": { color: "#405261", fontFamily: "Inter", fontSize: "12px", fontWeight: '400', paddingBottom: '9px', } }}>
              <TableCell style={{ paddingLeft: '0px' }}>Device</TableCell>
              <TableCell>Browser / OS</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? skeletonRows.map((_, index) => (
                <TableRow key={index} hover>
                  {Array.from({ length: 6 }).map((__, i) => (
                    <TableCell key={i}>
                      <Skeleton width="70%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
              : sortedSessions.map((row) => {
                const isCurrent = row.sessionId === currentSessionId;
                return (
                  <TableRow
                    key={row._id}
                    hover
                    sx={{
                      "&:hover": {
                        backgroundColor: "#F9FAFA",
                      },
                      "&:hover td": {
                        backgroundColor: "#F9FAFA",
                      },
                      "& td": {
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        height: "64px",
                      },
                      "& .MuiTableCell-body:last-of-type": {
                        width: '150px',
                        paddingRight: "24px",
                      },
                    }}>
                    <TableCell style={{ fontWeight: '500', paddingLeft: 0, color: '#061522' }}>{`${row.os} – ${row.deviceType}`}</TableCell>
                    <TableCell>{row.browser}</TableCell>
                    <TableCell>{row.ip}</TableCell>
                    <TableCell>{row.location?.city || "Unknown"}</TableCell>
                    <TableCell>
                      {new Date(row.lastUsed)
                        .toLocaleString("en-US", {
                          hour: "numeric",
                          minute: "numeric",
                          second: "numeric",
                          year: "numeric",
                          month: "numeric",
                          day: "numeric",
                          hour12: true,
                        })
                        .replace("AM", "am")
                        .replace("PM", "pm")}
                    </TableCell>

                    <TableCell>
                      {isCurrent ? (
                        "Current Session"
                      ) : (
                        <Button
                          variant="outlined"
                          sx={{
                            cursor: "pointer",
                            width: '104px',
                            height: '40px',
                            borderColor: '#D5DCE1',
                            color: '#405261',
                            fontFamily: 'Inter',
                            fontWeight: 400,
                            fontSize: '14px',
                            textTransform: 'none',
                            borderRadius: '8px',
                            bgcolor: '#FFFFFF',
                            "&:hover": {
                              bgcolor: '#FFFFFF',
                              borderColor: '#C5CED6',
                            },

                            "&.Mui-focusVisible, &:active": {
                              bgcolor: '#FFFFFF',
                            },
                          }}
                          size="small"
                          onClick={() => handleLogoutDevice(row.sessionId)}
                        >
                          Log out
                        </Button>

                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
